import { useCallback, useEffect, useMemo } from 'react';
import { RESTAPIError } from '@gonfalon/errors/RESTAPIError';
import type { TracePromotionJobResponse } from '@gonfalon/openapi/types';
import { useLatestRef } from '@gonfalon/react/useLatestRef';
import { getTracePromotionJobQuery, usePromoteTraceToDataset } from '@gonfalon/rest-api/queries/datasets';
import { useQuery } from '@tanstack/react-query';

import type { AddToDatasetDraft } from './types';

export type { TracePromotionJobResponse };

export class TracePromotionError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'TracePromotionError';
  }
}

const DEFAULT_POLL_INTERVAL_MS = 1000;

const isTerminal = (status: TracePromotionJobResponse['status'] | undefined) =>
  status === 'completed' || status === 'failed';

const toRequestError = (error: unknown) => {
  if (error instanceof RESTAPIError) {
    return new TracePromotionError(error.code, error.message);
  }
  return new TracePromotionError('TRACE_PROMOTION_FAILED', error instanceof Error ? error.message : 'Promotion failed');
};

const toJobError = (job: TracePromotionJobResponse) =>
  new TracePromotionError(job.error?.code ?? 'TRACE_PROMOTION_FAILED', job.error?.message ?? 'Promotion failed');

/**
 * Where a promotion is, as a single value. Every other piece of the hook reads off this instead of
 * juggling booleans: `isPromoting` is one comparison, and the settle effect fires on exactly the two
 * terminal variants.
 */
type PromotionState =
  | { status: 'idle' }
  | { status: 'promoting' }
  | { status: 'completed'; job: TracePromotionJobResponse }
  | { status: 'failed'; error: TracePromotionError };

function announcePromotionSettlement(
  promotion: PromotionState,
  callbacks: {
    current: {
      onCompleted?: (job: TracePromotionJobResponse) => void;
      onFailed?: (error: TracePromotionError) => void;
    };
  },
) {
  switch (promotion.status) {
    case 'completed':
      callbacks.current.onCompleted?.(promotion.job);
      break;
    case 'failed':
      callbacks.current.onFailed?.(promotion.error);
      break;
    default:
      break;
  }
}

export interface UseTracePromotionOptions {
  onCompleted?: (job: TracePromotionJobResponse) => void;
  onFailed?: (error: TracePromotionError) => void;
  pollIntervalMs?: number;
}

/**
 * Promotes one span into a dataset through the async promotion job: POST the row, then poll the job
 * until it settles. `promote` is ignored while a promotion is in flight. The hook holds one span's
 * promotion, so mount it under a key that changes with the span (see `ReviewTab`) — a new span then
 * starts from a clean slate.
 */
export function useTracePromotion({
  onCompleted,
  onFailed,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}: UseTracePromotionOptions = {}) {
  const callbacks = useLatestRef({ onCompleted, onFailed });

  const {
    mutate,
    data: accepted,
    variables: activeRequest,
    isPending: isRequesting,
    error: requestError,
  } = usePromoteTraceToDataset();

  const hasActiveJob = accepted !== undefined;

  const jobQuery = useQuery({
    ...getTracePromotionJobQuery({
      projKey: activeRequest?.projKey ?? '',
      envKey: activeRequest?.envKey ?? '',
      datasetId: activeRequest?.datasetId ?? '',
      jobId: accepted?.jobId ?? '',
    }),
    enabled: hasActiveJob,
    // Poll until the job reaches a terminal status; a settled job never needs re-fetching, so it
    // stops on terminal and does not refetch on focus. Transient poll errors keep the last status
    // and just retry on the next interval, so they can't end the promotion.
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (isTerminal(query.state.data?.status) ? false : pollIntervalMs),
  });

  const job = hasActiveJob ? jobQuery.data : undefined;

  // The whole lifecycle in one derived value: the accepted POST plus the polled job. Nothing is
  // stored on the side, so there is no promotion state that can drift from the mutation and query.
  const promotion = useMemo<PromotionState>(() => {
    // A POST in flight always wins, even a retry started after an earlier settle: until it is
    // accepted, the mutation may still expose the previous job's terminal state, and that stale
    // job must not resurface as this promotion's result.
    if (isRequesting) {
      return { status: 'promoting' };
    }
    // A rejected POST outranks any job status: there is no job to speak of.
    if (requestError) {
      return { status: 'failed', error: toRequestError(requestError) };
    }
    switch (job?.status) {
      case 'completed':
        return { status: 'completed', job };
      case 'failed':
        return { status: 'failed', error: toJobError(job) };
      case 'queued':
      case 'processing':
        return { status: 'promoting' };
      default:
        // Accepted and awaiting the first poll.
        return hasActiveJob ? { status: 'promoting' } : { status: 'idle' };
    }
  }, [requestError, job, isRequesting, hasActiveJob]);

  // The one place a terminal promotion is announced. `promotion` only takes a new identity when the
  // mutation or poll actually moves it, and a settled job stops polling, so completed/failed is
  // stable until the next `promote` — this fires once per settle.
  useEffect(() => {
    announcePromotionSettlement(promotion, callbacks);
  }, [promotion, callbacks]);

  const promote = useCallback(
    (draft: AddToDatasetDraft) => {
      if (promotion.status === 'promoting') {
        return;
      }
      mutate({
        projKey: draft.projectKey,
        envKey: draft.envKey,
        datasetId: draft.datasetId,
        body: {
          trace_id: draft.traceId,
          span_id: draft.spanId,
          timestamp: draft.timestamp,
          ...(draft.annotationId ? { annotation_id: draft.annotationId } : {}),
        },
      });
    },
    [mutate, promotion.status],
  );

  return {
    promote,
    isPromoting: promotion.status === 'promoting',
  };
}
